import { forwardRef, type ReactNode } from "react";
import { Button } from "@design-lab/shared/ui";
import { useDropzone } from "react-dropzone";
import { Shirt } from "lucide-react";
import { DESIGN_LAB_ACCEPT_CONFIG } from "common/artwork-file-types";
import { CanvaButton } from "@design-lab/features/upload-artwork/ui/Canva/CanvaButton";

export type TransferImageSelectorProps = {
  title?: ReactNode;
  onFileDrop: (files: File[]) => Promise<void>;
  isLoggedIn: boolean;
  isDesktop: boolean;
  onCanvaFilesReady?: (files: File[]) => Promise<void>;
  onLibraryClick?: () => void;
  allowMultipleFiles?: boolean;
};

export const TransferImageSelector = forwardRef<HTMLElement, TransferImageSelectorProps>(
  (
    {
      title,
      onFileDrop,
      isLoggedIn,
      isDesktop,
      onCanvaFilesReady,
      onLibraryClick,
      allowMultipleFiles = false
    },
    ref
  ) => {
    const { getRootProps, getInputProps, open } = useDropzone({
      accept: DESIGN_LAB_ACCEPT_CONFIG,
      multiple: allowMultipleFiles,
      noClick: true,
      noKeyboard: true,
      onDrop: onFileDrop
    });

    return (
      <section
        ref={ref}
        onDragEnter={getRootProps().onDragEnter}
        onDragOver={getRootProps().onDragOver}
        onDrop={getRootProps().onDrop}
        className="flex h-max w-full flex-1 border-spacing-12 flex-col items-center justify-center gap-2 rounded-md bg-blue-50 p-3 lg:gap-4 lg:border-2 lg:border-dashed lg:border-blue-700"
      >
        <div className="flex items-center gap-2">
          <Shirt className="size-6" />
          {title && <p className="font-bold">{title}</p>}
        </div>
        <p className="text-sm font-medium">Drag your design here or choose a design from</p>

        <div className="flex gap-1 lg:gap-2">
          <Button
            data-testid="my-device-button"
            size={isDesktop ? "xl" : "sm"}
            onPress={open}
            className="relative px-2 text-xs lg:px-4 lg:text-sm"
          >
            <input {...getInputProps()} />
            My Device
          </Button>

          {isLoggedIn && (
            <>
              {onLibraryClick && (
                <Button
                  data-testid="my-library-button"
                  size={isDesktop ? "xl" : "sm"}
                  variant="outline"
                  onPress={onLibraryClick}
                  className="px-2 text-xs lg:px-4 lg:text-sm"
                >
                  My Design Library
                </Button>
              )}
              {onCanvaFilesReady && (
                <CanvaButton
                  autoOpen={false}
                  onFilesReady={onCanvaFilesReady}
                  size={isDesktop ? "xl" : "sm"}
                  allowMultipleSelection={true}
                  maxSelectionCount={10}
                />
              )}
            </>
          )}
        </div>
      </section>
    );
  }
);

TransferImageSelector.displayName = "TransferImageSelector";
